import React, { useEffect, useState } from 'react';
import { Check, Copy, Link2, Users } from 'lucide-react';
import Modal from './Modal';
import Button from './Button';
import { useAuth } from '../../../auth/AuthProvider';
import { useCreateShare } from '../hooks/useCreateShare';

// Creator-side "Share this course" dialog. Mints (or returns) the canonical
// share link and lets the creator copy it. Recipients who open the link land
// on /shared/:token and regenerate their own copy of the course.
export default function ShareModal({ open, onClose, courseId, courseTitle }) {
  const { enabled } = useAuth();
  const { create, status, share, error } = useCreateShare();
  const [copied, setCopied] = useState(false);

  // Mint the link when the modal opens (once per course).
  useEffect(() => {
    if (open && courseId && status === 'idle') {
      create(courseId, { authEnabled: enabled });
    }
    if (!open) setCopied(false);
  }, [open, courseId, status, create, enabled]);

  const handleCopy = async () => {
    if (!share?.url) return;
    try {
      await navigator.clipboard.writeText(share.url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard blocked — the input is selectable as a fallback.
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Share this course"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Done
        </Button>
      }
    >
      <p style={{ margin: '0 0 16px', fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        Anyone with this link can preview{' '}
        <strong style={{ color: 'var(--text-primary)' }}>{courseTitle || 'your course'}</strong>{' '}
        and build their own copy on their Graspd account.
      </p>

      {status === 'error' ? (
        <div
          style={{
            padding: '12px 14px',
            borderRadius: 'var(--radius-control)',
            background: 'var(--danger-bg)',
            color: 'var(--danger)',
            fontSize: 13,
          }}
        >
          {error?.message || 'Could not create a share link. Please try again.'}
        </div>
      ) : (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '10px 12px',
            borderRadius: 'var(--radius-panel)',
            background: 'var(--glass-bg-medium)',
            border: '1px solid var(--border-subtle)',
          }}
        >
          <Link2 size={16} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
          <input
            readOnly
            value={status === 'ready' ? share?.url || '' : 'Creating link…'}
            onFocus={(e) => e.target.select()}
            aria-label="Shareable course link"
            style={{
              flex: 1,
              minWidth: 0,
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--text-primary)',
              fontSize: 13,
              fontFamily: 'inherit',
            }}
          />
          <Button
            size="sm"
            onClick={handleCopy}
            disabled={status !== 'ready'}
            style={{ flexShrink: 0 }}
          >
            {copied ? <Check size={14} /> : <Copy size={14} />}
            {copied ? 'Copied' : 'Copy'}
          </Button>
        </div>
      )}

      {status === 'ready' && (share?.acceptCount ?? 0) > 0 && (
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 6,
            marginTop: 14,
            fontSize: 13,
            color: 'var(--text-secondary)',
          }}
        >
          <Users size={14} style={{ color: 'var(--text-tertiary)' }} />
          {share.acceptCount} {share.acceptCount === 1 ? 'person has' : 'people have'} taken this from your share
        </div>
      )}

      <p style={{ margin: '16px 0 0', fontSize: 12, color: 'var(--text-tertiary)', lineHeight: 1.6 }}>
        Each person gets a fresh course generated on their own account, so their progress stays private to them.
      </p>
    </Modal>
  );
}
